import React from 'react';
import './profile.scss';
import { object, ref, string } from 'yup';
import { Form, Formik } from 'formik';
import { Box, Button, Grid, IconButton, InputAdornment, Paper, Typography } from '@mui/material';
import { PasswordDto } from '../../../services/dtos/form/password.dto';
import {
    controlLengthMax,
    controlLengthMin,
    controlLowerCase,
    controlNumeric,
    controlSpecific,
    controlUpperCase,
} from '../../../utils/form-validators.utils';
import { PasswordCriterionsDto } from '../../../services/dtos/password-criterions.dto';
import InputForm from '../../commun/input-form';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { ERROR_MAIL } from '../../../utils/constants.utils';
import { UserDto } from '../../../services/dtos/user.dto';

//Props d'entrée de la fonction PasswordForm
type Props = {
    user: UserDto;
    handlerSubmit: (e: PasswordDto) => void;
    passwordCriterions: PasswordCriterionsDto | null;
    infosPassword: JSX.Element | null;
    handlerOnChangePassword: (password: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    passwordFormSend: boolean;
    isRU?: boolean; // Ajout pour détection mode RU
};

/**
 * Composant qui gère la modification de mot de passe pour un profile.
 * @param user
 * @param handlerSubmit handle sur le submit
 * @param passwordCriterions critères de validation de mot de passe
 * @param infosPassword tooltip pour le champ mot de passe
 * @param handlerOnChangePassword handler sur le onChange
 * @param passwordFormSend permet de savoir si le form à était envoyé
 * @param isRU permet de savoir si on est en mode RU (connexion RU depuis Crusoé)
 * @constructor
 */
function PasswordForm({
    user,
    handlerSubmit,
    passwordCriterions,
    infosPassword,
    handlerOnChangePassword,
    passwordFormSend,
    isRU = false,
}: Props) {
    // etat de la visibilité du mot de passe
    const [showPassword, setShowPassword] = React.useState(false);

    // mise à jour de la visibilité du mot de passe
    const handleClickShowPassword = () => setShowPassword(!showPassword);

    // On réinitialise d'autres variable pour les autres champs, afin que leurs visibilité soient indépendantes les unes des autres
    const [showPasswordNew, setShowPasswordNew] = React.useState(false);
    const handleClickShowPasswordNew = () => setShowPasswordNew(!showPasswordNew);

    const [showPasswordNewConfirmation, setShowPasswordNewConfirmation] = React.useState(false);
    const handleClickShowPasswordNewConfirmation = () => setShowPasswordNewConfirmation(!showPasswordNewConfirmation);

    //Règles de validation du formulaire
    const validationSchema = object({
        ancienMail: string().required("L'adresse e-mail est obligatoire"),
        mail: string()
            .email(ERROR_MAIL)
            .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, ERROR_MAIL),
        confirmeMail: string()
            .trim()
            .when('mail', {
                is: (mail: string) => !!mail, // Vérifie si motDePasse a une valeur
                then: schema =>
                    schema
                        .required("La confirmation de l'adresse e-mail est obligatoire")
                        // Test si les mots de passe correspondent
                        .oneOf([ref('mail')], 'Les adresses e-mails ne correspondent pas'),
                otherwise: schema => schema.notRequired(), // Sinon, le champ n'est pas requis
            }),
        // Test si les mot de passe correspondent ou pas

        ancienMotDePasse: isRU
            ? string().notRequired() // En mode RU, le mot de passe actuel n'est pas requis
            : string().required('Le mot de passe est obligatoire'),
        //Contrôle du champ mot de passe
        //Contrôle : obligatoire / règles en fonction des critères récupérée
        motDePasse: string()
            .trim()
            // Si le mot de passe ne contient pas de majuscule
            .test(
                'upper-case',
                'Le mot de passe doit contenir au moins une lettre en majuscules',
                value => !value || value === '' || controlUpperCase(value, passwordCriterions?.upperCase)
            )
            // Si le mot de passe ne contient pas de minuscule
            .test(
                'lower-case',
                'Le mot de passe doit contenir au moins une lettre en minuscules',
                value => !value || value === '' || controlLowerCase(value, passwordCriterions?.lowerCase)
            )
            // Si le mot de passe ne contient pas de chiffre
            .test(
                'number',
                'Le mot de passe doit contenir au moins un chiffre',
                value => !value || value === '' || controlNumeric(value, passwordCriterions?.number)
            )
            // Si le mot de passe ne contient pas de symbole
            .test(
                'symbole',
                'Le mot de passe doit contenir au moins un caractère spécial : !@#%&',
                value => !value || value === '' || controlSpecific(value, passwordCriterions?.specialChars)
            )
            // Si le mot de passe ne contient pas assez de caractères
            .test(
                'lengthMin',
                'Le mot de passe doit contenir au moins ' + passwordCriterions?.minLength + ' caractères',
                value => !value || value === '' || controlLengthMin(value, passwordCriterions?.minLength)
            )
            // Si le mot de passe ne contient trop de caractères
            .test(
                'lengthMax',
                'Le mot de passe avoir au maximum ' + passwordCriterions?.maxLength + ' caractères',
                value => !value || value === '' || controlLengthMax(value, passwordCriterions?.maxLength)
            ),
        //Controle confirmation de mot de passe
        confirmeMotDePasse: string()
            .trim()
            .when('motDePasse', {
                is: (motDePasse: string) => !!motDePasse, // Vérifie si motDePasse a une valeur
                then: schema =>
                    schema
                        .required('La confirmation du mot de passe est obligatoire')
                        // Test si les mots de passe correspondent
                        .oneOf([ref('motDePasse')], 'Les mots de passe ne correspondent pas'),
                otherwise: schema => schema.notRequired(), // Sinon, le champ n'est pas requis
            }),
    });

    //Initialisation de valeurs
    const initialValues = {
        mail: '',
        confirmeMail: '',
        ancienMail: user.identifiant ? user.identifiant : '',
        ancienMotDePasse: '',
        motDePasse: '',
        confirmeMotDePasse: '',
    };

    return (
        //Formulaire de changement de mot de passe
        <Box className="contact-details">
            <Paper>
                <Formik initialValues={initialValues} onSubmit={handlerSubmit} validationSchema={validationSchema}>
                    <Form>
                        <Grid container spacing={2}>
                            {!isRU && (
                                <>
                                    <Grid item xs={12} md={7}>
                                        <Typography variant={'h5'}>Mot de passe actuel</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={5} sx={{ textAlign: 'right' }}>
                                        <Typography variant={'subtitle2'}>* : champs obligatoires</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <InputForm
                                            required
                                            id="ancienMotDePasse"
                                            name="ancienMotDePasse"
                                            label="Mot de passe actuel"
                                            type={showPassword ? 'text' : 'password'}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            data-testid="old-eye-password"
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            edge="end"
                                                        >
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={7}></Grid>
                                </>
                            )}
                            {isRU && (
                                <>
                                    <Grid item xs={12} md={12}>
                                        <Typography variant={'body1'} sx={{ color: 'info.main', mb: 2 }}>
                                            Mode administrateur : Vous pouvez modifier les identifiants de connexion
                                            sans avoir besoin du mot de passe actuel.
                                        </Typography>
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} md={7}>
                                <Typography variant={'h5'}>Modifier son adresse e-mail de connexion</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}></Grid>
                            <Grid item xs={12} md={4}>
                                <InputForm
                                    disabled={true}
                                    required
                                    id="ancienMail"
                                    name="ancienMail"
                                    label="Adresse e-mail actuelle"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputForm id="mail" name="mail" label="Nouvelle adresse e-mail" />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputForm
                                    id="confirmeMail"
                                    name="confirmeMail"
                                    label="Confirmation de la nouvelle adresse e-mail"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant={'h5'}>Modifier mon mot de passe</Typography>
                            </Grid>
                            <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}></Grid>

                            <Grid item xs={12} md={4}>
                                <InputForm
                                    id="motDePasse"
                                    name="motDePasse"
                                    label="Nouveau mot de passe"
                                    type={showPasswordNew ? 'text' : 'password'}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    data-testid="new-eye-password"
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPasswordNew}
                                                    edge="end"
                                                >
                                                    {showPasswordNew ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    tooltipMessage={infosPassword}
                                    handleChange={handlerOnChangePassword}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputForm
                                    id="confirmeMotDePasse"
                                    name="confirmeMotDePasse"
                                    label="Confirmation du nouveau mot de passe"
                                    type={showPasswordNewConfirmation ? 'text' : 'password'}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    data-testid="new-confirmation-eye-password"
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPasswordNewConfirmation}
                                                    edge="end"
                                                >
                                                    {showPasswordNewConfirmation ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    tooltipMessage={infosPassword}
                                />
                            </Grid>
                            <Grid item xs={12} className={'buttons-form'}>
                                <Typography variant={'button'}>
                                    <Button type="submit" variant="contained" disabled={passwordFormSend}>
                                        Valider
                                    </Button>
                                </Typography>
                            </Grid>
                        </Grid>
                    </Form>
                </Formik>
            </Paper>
        </Box>
    );
}

export default PasswordForm;
